import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { KeyValue } from "@/main/models/common";
import {
	CountryByVehicleFilterRequest,
	SubModelRequest,
	VehicleFiltersRequest,
	VehicleYearDomainRequest,
	VehicleYearRequest,
} from "@/main/models/request";
import { getTranslateByKey, isNullUndefined, removeEmptyValues } from "@/main/services/utils";
import { VehicleAutoCompleteResponse } from "@/main/models/response";
import { VehicleAutoCompleteTypeEnum } from "@/main/services/enum/VehicleAutoCompleteTypeEnum";

import KogAutoCompleteControlled from "../inputAutoComplete/autoCompleteControlled";
import KogAutoCompleteVehicleControlled from "../inputAutoComplete/autoCompleteVehicleControlled";
import VehicleAutoCompleteComponent from "../vehicle/VehicleAutoCompleteComponent";
import useVehicleFilterOptions from "./useVehicleFilterOptions";
import * as S from "./styles";

export interface IVehicleFilterFieldNames {
	country: string;
	manufacturer: string;
	model: string;
	trim: string;
	year: string;
}

export const VEHICLE_FILTER_DEFAULT_FIELD_NAMES: IVehicleFilterFieldNames = {
	country: "vehicleCountry",
	manufacturer: "manufacturer",
	model: "model",
	trim: "trim",
	year: "year",
};

interface IVehicleFilterFieldsProps {
	idPrefix: string;
	fieldNames?: Partial<IVehicleFilterFieldNames>;
	isRequired?: boolean;
	isRequiredCountry?: boolean;
	isDisabled?: boolean;
	isDisabledCountry?: boolean;
	isDisabledManufacturer?: boolean;
	isDisabledModel?: boolean;
	isDisabledTrim?: boolean;
	isDisabledYear?: boolean;
	showVehicleSearch?: boolean;
	guidCountryToSelect?: string | null;
	onVehicleChange?: () => void;
}

function VehicleFilterFields({
	idPrefix,
	fieldNames,
	isRequired = false,
	isRequiredCountry = isRequired,
	isDisabled = false,
	isDisabledCountry = false,
	isDisabledManufacturer = false,
	isDisabledModel = false,
	isDisabledTrim = false,
	isDisabledYear = false,
	showVehicleSearch = false,
	guidCountryToSelect,
	onVehicleChange,
}: IVehicleFilterFieldsProps) {
	const names = { ...VEHICLE_FILTER_DEFAULT_FIELD_NAMES, ...fieldNames };
	const {
		control,
		setValue,
		getValues,
		formState: { errors },
	} = useFormContext();
	const {
		countries,
		manufacturers,
		models,
		subModels,
		years,
		isLoadingCountry,
		isLoadingManufacturer,
		isLoadingModel,
		isLoadingSubModel,
		isLoadingYear,
		loadCountries,
		loadManufacturers,
		loadModels,
		loadSubModels,
		loadYears,
		loadYearsDomain,
		clearModels,
		clearSubModels,
	} = useVehicleFilterOptions();

	const watcherCountry = useWatch({
		control,
		name: names.country,
	}) as KeyValue | null;
	const watcherManufacturer = useWatch({
		control,
		name: names.manufacturer,
	}) as KeyValue | null;
	const watcherModel = useWatch({
		control,
		name: names.model,
	}) as KeyValue | null;
	const watcherTrim = useWatch({
		control,
		name: names.trim,
	}) as KeyValue | null;
	const watcherYear = useWatch({
		control,
		name: names.year,
	}) as KeyValue | null;

	const hasSingleCountry = !isNullUndefined(countries) && countries.length === 1;
	const guidCountry = isNullUndefined(watcherCountry) ? undefined : String(watcherCountry.key);

	function getSelectedKey(option: KeyValue | null) {
		return isNullUndefined(option) ? undefined : String(option.key);
	}

	function generateOption(key?: string | null, value?: string | null) {
		if (isNullUndefined(key) || isNullUndefined(value) || key === "") return null;

		return { key, value };
	}

	function hasRequiredError(fieldName: string) {
		return errors[fieldName]?.type === "required";
	}

	function getRequiredMessage(fieldName: string, translateKey: string) {
		return hasRequiredError(fieldName) ? getTranslateByKey(translateKey, true) : "";
	}

	function loadCountriesByFilters() {
		const filter: CountryByVehicleFilterRequest = removeEmptyValues({
			guidVehicleManufacturer: getSelectedKey(watcherManufacturer),
			guidVehicleModel: getSelectedKey(watcherModel),
			guidVehicleSubModel: getSelectedKey(watcherTrim),
			guidVehicleYear: getSelectedKey(watcherYear),
		});

		loadCountries(filter);
	}

	function loadManufacturersByFilters() {
		const filter: VehicleFiltersRequest = removeEmptyValues({
			guidVehicleManufacturer: null,
			guidVehicleModel: getSelectedKey(watcherModel),
			guidVehicleSubModel: getSelectedKey(watcherTrim),
			guidVehicleYear: getSelectedKey(watcherYear),
			guidCountry,
		});

		loadManufacturers(filter);
	}

	function loadModelsByFilters() {
		if (isNullUndefined(watcherManufacturer)) {
			clearModels();
			return;
		}

		const filter: VehicleFiltersRequest = removeEmptyValues({
			guidVehicleManufacturer: getSelectedKey(watcherManufacturer),
			guidVehicleModel: null,
			guidVehicleSubModel: getSelectedKey(watcherTrim),
			guidVehicleYear: getSelectedKey(watcherYear),
			guidCountry,
		});

		loadModels(filter);
	}

	function loadSubModelsByFilters() {
		if (isNullUndefined(watcherManufacturer) || isNullUndefined(watcherModel)) {
			clearSubModels();
			return;
		}

		const filter: SubModelRequest = removeEmptyValues({
			guidManufacturer: getSelectedKey(watcherManufacturer),
			guidModel: getSelectedKey(watcherModel),
			guidYear: getSelectedKey(watcherYear),
			guidCountry,
		});

		loadSubModels(filter);
	}

	function loadYearsByFilters() {
		if (!isNullUndefined(watcherManufacturer) && !isNullUndefined(watcherModel)) {
			const filter: VehicleYearRequest = removeEmptyValues({
				guidManufacturer: String(watcherManufacturer.key),
				guidModel: String(watcherModel.key),
				guidSubModel: getSelectedKey(watcherTrim),
				guidCountry,
			});

			loadYears(filter);
			return;
		}

		const hasNoVehicleSelected =
			isNullUndefined(watcherManufacturer) && isNullUndefined(watcherModel) && isNullUndefined(watcherTrim);
		if (!hasNoVehicleSelected) return;

		const domainFilter: VehicleYearDomainRequest = removeEmptyValues({
			guidCountry,
		});

		loadYearsDomain(domainFilter);
	}

	const hasVehicleFilterSelected =
		!isNullUndefined(watcherManufacturer) ||
		!isNullUndefined(watcherModel) ||
		!isNullUndefined(watcherTrim) ||
		!isNullUndefined(watcherYear);
	const isCountryFieldDisabled =
		isDisabled || isDisabledCountry || (hasSingleCountry && !hasVehicleFilterSelected) || isLoadingCountry;
	const isYearFieldDisabled = isDisabled || isDisabledYear || isLoadingYear;
	const isManufacturerFieldDisabled = isDisabled || isDisabledManufacturer || isLoadingManufacturer;
	const isModelFieldDisabled = isDisabled || isDisabledModel || isLoadingModel;
	const isTrimFieldDisabled = isDisabled || isDisabledTrim || isLoadingSubModel;

	function handleVehicleSearchChange(vehicle: VehicleAutoCompleteResponse | null) {
		if (isNullUndefined(vehicle)) return;

		setValue(names.country, generateOption(vehicle.guidVehicleCountry, vehicle.nameVehicleCountry));
		setValue(names.year, generateOption(vehicle.guidYear, vehicle.year));
		setValue(names.manufacturer, generateOption(vehicle.guidVehicleManufacturer, vehicle.manufacturer));
		setValue(names.model, generateOption(vehicle.guidVehicleModel, vehicle.model));
		setValue(names.trim, generateOption(vehicle.guidSubModel, vehicle.subModel));

		if (onVehicleChange) onVehicleChange();
	}

	useEffect(() => {
		loadCountriesByFilters();
	}, [watcherManufacturer?.key, watcherModel?.key, watcherTrim?.key, watcherYear?.key]);

	useEffect(() => {
		if (isNullUndefined(countries)) return;

		const selectedCountry = getValues(names.country) as KeyValue | null;
		if (!isNullUndefined(selectedCountry)) {
			const isSelectedAvailable = countries.some((option) => String(option.key) === String(selectedCountry.key));
			if (!isSelectedAvailable) setValue(names.country, null);
			return;
		}

		const optionFromGuid = isNullUndefined(guidCountryToSelect)
			? null
			: countries.find((option) => String(option.key) === String(guidCountryToSelect));
		const singleCountryOption = hasSingleCountry ? countries[0] : null;
		const optionToSelect = optionFromGuid ?? singleCountryOption;
		if (isNullUndefined(optionToSelect)) return;

		setValue(names.country, optionToSelect);
	}, [countries, guidCountryToSelect]);

	useEffect(() => {
		loadManufacturersByFilters();
	}, [guidCountry, watcherModel?.key, watcherTrim?.key, watcherYear?.key]);

	useEffect(() => {
		loadModelsByFilters();
	}, [guidCountry, watcherManufacturer?.key, watcherTrim?.key, watcherYear?.key]);

	useEffect(() => {
		loadSubModelsByFilters();
	}, [guidCountry, watcherManufacturer?.key, watcherModel?.key, watcherYear?.key]);

	useEffect(() => {
		loadYearsByFilters();
	}, [guidCountry, watcherManufacturer?.key, watcherModel?.key, watcherTrim?.key]);

	return (
		<>
			{showVehicleSearch && (
				<S.ContainerVehicleSearch>
					<VehicleAutoCompleteComponent
						handleChangeVehicle={handleVehicleSearchChange}
						vehicleAutoCompleteType={VehicleAutoCompleteTypeEnum.Full}
						hasBorderStyle
						label={getTranslateByKey("information.vehicle")}
						disabled={isDisabled}
					/>
				</S.ContainerVehicleSearch>
			)}
			<KogAutoCompleteControlled
				id={`${idPrefix}-autocomplete-country`}
				nameCustom={names.country}
				hasErrorCustom={hasRequiredError(names.country)}
				errorMessageCustom={getRequiredMessage(names.country, "information.countryRequired")}
				label={getTranslateByKey("information.countryVehicle")}
				control={control}
				rulesCustom={{ required: isRequiredCountry }}
				options={countries}
				isLoading={isLoadingCountry}
				onChangeCustom={() => {}}
				setValueCustom={setValue}
				disabled={isCountryFieldDisabled}
			/>
			<KogAutoCompleteControlled
				id={`${idPrefix}-autocomplete-year`}
				nameCustom={names.year}
				hasErrorCustom={hasRequiredError(names.year)}
				errorMessageCustom={getRequiredMessage(names.year, "information.yearRequired")}
				label={getTranslateByKey("information.year")}
				control={control}
				rulesCustom={{ required: isRequired }}
				options={years}
				isLoading={isLoadingYear}
				onChangeCustom={() => {
					if (onVehicleChange) onVehicleChange();
				}}
				setValueCustom={setValue}
				disabled={isYearFieldDisabled}
			/>
			<KogAutoCompleteVehicleControlled
				id={`${idPrefix}-autocomplete-manufacturer`}
				nameCustom={names.manufacturer}
				hasErrorCustom={hasRequiredError(names.manufacturer)}
				errorMessageCustom={getRequiredMessage(names.manufacturer, "information.manufacturerRequired")}
				label={getTranslateByKey("information.manufacturer")}
				control={control}
				rulesCustom={{ required: isRequired }}
				options={manufacturers}
				isLoading={isLoadingManufacturer}
				onChangeCustom={() => {
					if (onVehicleChange) onVehicleChange();
				}}
				setValueCustom={setValue}
				disabled={isManufacturerFieldDisabled}
			/>
			<KogAutoCompleteVehicleControlled
				id={`${idPrefix}-autocomplete-model`}
				nameCustom={names.model}
				hasErrorCustom={hasRequiredError(names.model)}
				errorMessageCustom={getRequiredMessage(names.model, "information.modelRequired")}
				label={getTranslateByKey("information.model")}
				control={control}
				rulesCustom={{ required: isRequired }}
				options={models}
				isLoading={isLoadingModel}
				onChangeCustom={() => {
					if (onVehicleChange) onVehicleChange();
				}}
				setValueCustom={setValue}
				disabled={isModelFieldDisabled}
			/>
			<KogAutoCompleteVehicleControlled
				id={`${idPrefix}-autocomplete-trim`}
				nameCustom={names.trim}
				hasErrorCustom={hasRequiredError(names.trim)}
				errorMessageCustom={getRequiredMessage(names.trim, "information.trimRequired")}
				label={getTranslateByKey("information.trim")}
				control={control}
				rulesCustom={{ required: isRequired }}
				options={subModels}
				isLoading={isLoadingSubModel}
				onChangeCustom={() => {
					if (onVehicleChange) onVehicleChange();
				}}
				setValueCustom={setValue}
				disabled={isTrimFieldDisabled}
			/>
		</>
	);
}

export default VehicleFilterFields;
